import { get, post, del } from "@/services/axiosUitils";
import type {
  SendInviteRequest,
  SendInviteResponse,
  AcceptInviteRequest,
  AcceptInviteResponse,
  RejectInviteRequest,
  InviteDetails,
  FamilyGroup,
  MembersListResponse,
  PendingInvitesResponse,
  MemberHealthData,
  MemberHealthResponse,
  FamilyMember,
} from "../types/family";

class FamilyService {
  private static instance: FamilyService;

  private constructor() {}

  public static getInstance(): FamilyService {
    if (!FamilyService.instance) {
      FamilyService.instance = new FamilyService();
    }
    return FamilyService.instance;
  }

  /**
   * Create a new family group
   */
  async createGroup(name: string): Promise<import("../types/family").CreateGroupResponse> {
    try {
      const response = await post<import("../types/family").CreateGroupResponse>(
        "/food_scanner/family/groups/",
        { name }
      );

      if (!response) {
        throw new Error("Failed to create group");
      }

      return response;
    } catch (error) {
      console.error("Error creating group:", error);
      throw error;
    }
  }

  /**
   * Get all groups for current user
   */
  async getGroups(): Promise<import("../types/family").GroupListResponse> {
    try {
      const response = await get<import("../types/family").GroupListResponse>(
        "/food_scanner/family/groups/"
      );

      if (!response) {
        return { success: false, groups: [] };
      }

      return response;
    } catch (error) {
      console.error("Error getting groups:", error);
      return { success: false, groups: [] };
    }
  }

  /**
   * Send family invite via email or link
   */
  async sendInvite(request: SendInviteRequest): Promise<SendInviteResponse> {
    try {
      const response = await post<SendInviteResponse>(
        "/food_scanner/family/invite/send/",
        request
      );

      if (!response) {
        throw new Error("Failed to send invite");
      }

      return response;
    } catch (error) {
      console.error("Error sending invite:", error);
      throw error;
    }
  }

  /**
   * Accept family invite using token
   */
  async acceptInvite(token: string): Promise<AcceptInviteResponse> {
    try {
      const request: AcceptInviteRequest = { token };
      const response = await post<AcceptInviteResponse>(
        "/food_scanner/family/invite/accept/",
        request
      );

      if (!response) {
        throw new Error("Failed to accept invite");
      }

      return response;
    } catch (error) {
      console.error("Error accepting invite:", error);
      throw error;
    }
  }

  /**
   * Reject family invite using token
   * Note: API endpoint may not be ready yet
   */
  async rejectInvite(token: string, reason?: string): Promise<AcceptInviteResponse> {
    try {
      const request: RejectInviteRequest = { token, reason };
      const response = await post<AcceptInviteResponse>(
        "/food_scanner/family/invite/reject/",
        request
      );

      if (!response) {
        throw new Error("Failed to reject invite");
      }

      return response;
    } catch (error) {
      console.error("Error rejecting invite:", error);
      throw error;
    }
  }

  /**
   * Get invite details by token (before accepting)
   * Note: API endpoint may not be ready yet
   */
  async getInviteDetails(token: string): Promise<InviteDetails> {
    try {
      const response = await get<InviteDetails>(
        `/food_scanner/family/invite/details/?token=${token}`,
        // Pre-accept detail fetch: stay silent on error (e.g. logged-out invitee
        // gets a 401 — the modal handles routing them through sign-in instead).
        { showNotification: false }
      );

      if (!response) {
        throw new Error("Failed to get invite details");
      }

      return response;
    } catch (error) {
      console.error("Error getting invite details:", error);
      throw error;
    }
  }

  /**
   * Get pending invites sent by current user
   * Note: API endpoint may not be ready yet
   */
  async getPendingInvites(): Promise<PendingInvitesResponse> {
    try {
      const response = await get<PendingInvitesResponse>(
        "/food_scanner/family/invite/pending/"
      );

      if (!response) {
        return { success: false, invites: [] };
      }

      return response;
    } catch (error) {
      console.error("Error getting pending invites:", error);
      return { success: false, invites: [] };
    }
  }

  /**
   * Cancel a pending invite
   * Note: API endpoint may not be ready yet
   */
  async cancelInvite(inviteId: string): Promise<boolean> {
    try {
      await del(`/food_scanner/family/invite/${inviteId}/`);
      return true;
    } catch (error) {
      console.error("Error canceling invite:", error);
      return false;
    }
  }

  /**
   * Send a nudge/message to family member
   * Note: API endpoint may not be ready yet
   */
  async sendNudge(memberId: string, message: string): Promise<boolean> {
    try {
      await post(`/food_scanner/family/nudge/${memberId}/`, { message });
      return true;
    } catch (error) {
      console.error("Error sending nudge:", error);
      return false;
    }
  }



  /**
   * Get pending meal approval details by token
   */
  async getMealApprovalDetails(token: string): Promise<import("../types/family").PendingMealApproval> {
    try {
      const response = await get<import("../types/family").PendingMealApproval>(
        `/food_scanner/family/meal-log/details/?token=${token}`
      );

      if (!response) {
        throw new Error("Failed to get meal approval details");
      }

      return response;
    } catch (error) {
      console.error("Error getting meal approval details:", error);
      throw error;
    }
  }

  /**
   * Approve a meal logged by another family member
   */
  async approveMeal(token: string): Promise<import("../types/family").MealApprovalResponse> {
    try {
      const response = await post<import("../types/family").MealApprovalResponse>(
        "/food_scanner/family/meal-log/approve/",
        { token }
      );

      if (!response) {
        throw new Error("Failed to approve meal");
      }

      return response;
    } catch (error) {
      console.error("Error approving meal:", error);
      throw error;
    }
  }

  /**
   * Deny a meal logged by another family member
   */
  async denyMeal(token: string, reason?: string): Promise<import("../types/family").MealApprovalResponse> {
    try {
      const response = await post<import("../types/family").MealApprovalResponse>(
        "/food_scanner/family/meal-log/reject/",
        { token, ...(reason != null && reason !== "" ? { reason } : {}) }
      );

      if (!response) {
        throw new Error("Failed to deny meal");
      }

      return response;
    } catch (error) {
      console.error("Error denying meal:", error);
      throw error;
    }
  }

  /**
   * Approve current user's membership in a group (when is_approve = true)
   */
  async approveMembership(groupId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await post<{ success: boolean; message: string }>(
        `/food_scanner/family/groups/${groupId}/approve-member/`,
        {}
      );

      if (!response) {
        throw new Error("Failed to approve membership");
      }

      return response;
    } catch (error) {
      console.error("Error approving membership:", error);
      throw error;
    }
  }



  /**
   * Reject current user's membership in a group (when is_approve = true)
   */
  async rejectMembership(groupId: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await post<{ success: boolean; message: string }>(
        `/food_scanner/family/groups/${groupId}/reject-member/`,
        {}
      );

      if (!response) {
        throw new Error("Failed to reject membership");
      }

      return response;
    } catch (error) {
      console.error("Error rejecting membership:", error);
      throw error;
    }
  }
}

export default FamilyService;
